import { initData, openLink, openTelegramLink } from "@tma.js/sdk-react";

import { getCurrencySymbol } from "shared/utils/get-currency-symbol";

import type {
  PlanCurrency,
  SubscriptionResponse,
  TributePlan,
} from "./types";

export const BOT_CHAT_URL = "https://t.me/resonar_bot";

export const isSubscriptionActive = (
  subscription: SubscriptionResponse | undefined
): boolean => {
  if (!subscription) return false;
  if (
    subscription.subscription !== "standard" &&
    subscription.subscription !== "pro"
  ) {
    return false;
  }
  return (
    !subscription.expiresAt ||
    new Date(subscription.expiresAt).getTime() > Date.now()
  );
};

export const detectCurrency = (): PlanCurrency => {
  const user = initData.user();
  const languageCode =
    user && "language_code" in user
      ? (user as { language_code?: string }).language_code
      : (user as { languageCode?: string } | undefined)?.languageCode;
  return languageCode === "ru" ? "rub" : "eur";
};

export const formatPlanPrice = (
  plan: TributePlan | undefined,
  currency: PlanCurrency
): string => {
  const amount = plan?.prices[currency];
  if (!amount) return "";
  return `${getCurrencySymbol(currency)}${amount / 100}`;
};

export const openPaymentUrl = (
  webappUrl: string | null,
  webUrl: string | null
) => {
  const url = webappUrl ?? webUrl;
  if (!url) return;
  if (url.startsWith("https://t.me")) {
    openTelegramLink(url);
    return;
  }
  openLink(url, { tryInstantView: false });
};

// Opening the Tribute checkout (a t.me link) CLOSES this mini app, so the
// "payment in flight" flag must survive a restart — it lives in localStorage
// and is picked up on the next launch to resume polling.
const AWAITING_PAYMENT_KEY = "tribute_awaiting_payment";
const AWAITING_PAYMENT_MAX_AGE_MS = 15 * 60 * 1000;

export const markAwaitingPayment = () => {
  try {
    localStorage.setItem(AWAITING_PAYMENT_KEY, String(Date.now()));
  } catch {
    // storage unavailable — polling just won't survive a restart
  }
};

export const clearAwaitingPayment = () => {
  try {
    localStorage.removeItem(AWAITING_PAYMENT_KEY);
  } catch {
    // ignore
  }
};

export const isAwaitingPaymentPersisted = (): boolean => {
  try {
    const raw = localStorage.getItem(AWAITING_PAYMENT_KEY);
    if (!raw) return false;
    const startedAt = parseInt(raw, 10);
    if (
      Number.isNaN(startedAt) ||
      Date.now() - startedAt > AWAITING_PAYMENT_MAX_AGE_MS
    ) {
      localStorage.removeItem(AWAITING_PAYMENT_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};
